// src/components/horizontalNav/HorizontalNav.jsx
//
// Top-bar navigation rendered when settings.themeLayout === 'horizontal'.
// Mirrors the SideBar's permission filter and active-route logic so the
// same items show under the same conditions, just laid out horizontally.
//
// Items with `children` open a Menu (click) instead of an inline expand.
// On screens narrower than `md` the parent Layout falls back to a mini
// sidebar so we never have to handle a cramped horizontal bar on mobile.

import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Button,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    useTheme,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import i18next from 'i18next';

import { sidebarItems } from '../../constants/sidebarItems';
import { usePermissions } from '../../auth/PermissionsContext';
import { useSettingsContext } from '../../setting/SettingsContext';

// Same as SideBar's permissionPrefixByPath. Kept in sync intentionally
// (extracting both into a shared constants file is a follow-up clean-up).
const PERMISSION_PREFIX_BY_PATH = {
    '/dashboard': null,
    '/setting': 'settings.',
    '/countries': 'countries.',
    '/currencies': 'currencies.',
    '/nationalities': 'nationalities.',
    '/pax-types': 'paxTypes.',
    '/product-types': 'productTypes.',
    '/transportations': 'transportations.',
    '/categories': 'categories.',
    '/brands': 'brands.',
    '/services': 'services.',
    '/suppliers': 'suppliers.',
    '/tour-options': 'tourOptions.',
    '/products': 'products.',
    '/option-groups': 'products.',
    '/programs': 'programs.',
    '/departments': 'departments.',
    '/users': 'users.',
    '/roles': 'roles.',
    '/collections': 'cms.',
    '/pages': 'cms.',
    '/settings/integrations/webhooks': 'webhooks.',
};

const HorizontalNav = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();

    const lng = i18next.language || 'en';
    const isArabic = lng === 'ar';

    const { hasPrefix, isInitialized, isAuthenticated } = usePermissions();
    const { themeNavColor } = useSettingsContext();
    const isApparent = themeNavColor === 'apparent';
    const isDark = theme.palette.mode === 'dark';

    // -------------------------------------------------------------------
    // Resolve color tokens once. The horizontal bar follows the same
    // Integrate / Apparent contract as the sidebar.
    // -------------------------------------------------------------------
    const navBg = isApparent
        ? (isDark ? '#0F172A' : '#FFFFFF')
        : theme.palette.primary.main;
    const navBgImage = isApparent
        ? 'none'
        : (isDark
            ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
            : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`);
    const navText = isApparent
        ? (isDark ? '#E5E7EB' : '#1C252E')
        : '#FFFFFF';
    const activeBg = isApparent
        ? `${theme.palette.primary.main}1f`
        : 'rgba(255,255,255,0.18)';
    const hoverBg = isApparent
        ? `${theme.palette.primary.main}14`
        : 'rgba(255,255,255,0.12)';
    const dividerColor = isApparent ? theme.palette.divider : 'rgba(255,255,255,0.18)';

    // -------------------------------------------------------------------
    // Permission-based filtering (same rules as SideBar).
    // -------------------------------------------------------------------
    const filteredItems = useMemo(() => {
        const canSeeItem = (item) => {
            const prefix = PERMISSION_PREFIX_BY_PATH[item.path];
            if (!prefix) return true;
            return hasPrefix(prefix);
        };
        return sidebarItems
            .map((item) => {
                const children = Array.isArray(item.children)
                    ? item.children.filter(canSeeItem)
                    : undefined;
                return { ...item, children };
            })
            .filter((item) => {
                const visible = canSeeItem(item);
                const hasVisibleChildren = item.children && item.children.length > 0;
                return visible || hasVisibleChildren;
            });
    }, [hasPrefix]);

    const blockMenuUntilReady = !isInitialized || !isAuthenticated;

    // -------------------------------------------------------------------
    // One Menu anchor per parent item with children. We track which
    // parent's menu is open by its `key` rather than per-item state.
    // -------------------------------------------------------------------
    const [openMenuKey, setOpenMenuKey] = useState(null);
    const [menuAnchor, setMenuAnchor] = useState(null);

    const handleParentClick = (event, item) => {
        const hasChildren = item.children && item.children.length > 0;
        if (hasChildren) {
            setOpenMenuKey(item.key);
            setMenuAnchor(event.currentTarget);
        } else {
            navigate(item.path);
        }
    };

    const closeMenu = () => {
        setOpenMenuKey(null);
        setMenuAnchor(null);
    };

    if (blockMenuUntilReady) return null;

    return (
        <Box
            component="nav"
            sx={{
                width: '100%',
                bgcolor: navBg,
                backgroundImage: navBgImage,
                color: navText,
                borderBottom: `1px solid ${dividerColor}`,
                boxShadow: isApparent
                    ? `0 1px 4px 0 ${isDark ? 'rgba(0,0,0,0.45)' : 'rgba(15,23,42,0.06)'}`
                    : `0 2px 10px 0 ${theme.palette.primary.main}33`,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    overflowX: 'auto',
                    px: 2,
                    py: 0.75,
                    gap: 0.5,
                    direction: isArabic ? 'rtl' : 'ltr',
                    // Hide scrollbar but keep scrollability
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${dividerColor} transparent`,
                    '&::-webkit-scrollbar': { height: 4 },
                    '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: dividerColor,
                        borderRadius: 999,
                    },
                }}
            >
                {filteredItems.map((item) => {
                    const Icon = item.icon;
                    const hasChildren = item.children && item.children.length > 0;
                    const childActive = hasChildren
                        ? item.children.some((ch) => location.pathname.startsWith(ch.path))
                        : false;
                    const active = (item.path && location.pathname.startsWith(item.path)) || childActive;

                    return (
                        <React.Fragment key={item.key}>
                            <Button
                                onClick={(e) => handleParentClick(e, item)}
                                startIcon={Icon ? <Icon fontSize="small" /> : null}
                                endIcon={hasChildren ? <ExpandMore fontSize="small" /> : null}
                                sx={{
                                    flexShrink: 0,
                                    color: navText,
                                    textTransform: 'none',
                                    fontSize: 14,
                                    fontWeight: 600,
                                    px: 1.5,
                                    py: 0.75,
                                    borderRadius: 1.5,
                                    bgcolor: active ? activeBg : 'transparent',
                                    '&:hover': { bgcolor: hoverBg },
                                    '& .MuiButton-startIcon': {
                                        marginInlineEnd: 0.75,
                                        marginInlineStart: 0,
                                    },
                                    '& .MuiButton-endIcon': {
                                        marginInlineEnd: 0,
                                        marginInlineStart: 0.5,
                                    },
                                }}
                            >
                                {item.label?.[lng] || item.label?.en}
                            </Button>

                            {hasChildren && (
                                <Menu
                                    anchorEl={menuAnchor}
                                    open={openMenuKey === item.key}
                                    onClose={closeMenu}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: isArabic ? 'right' : 'left',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: isArabic ? 'right' : 'left',
                                    }}
                                    PaperProps={{
                                        sx: {
                                            mt: 0.5,
                                            minWidth: 220,
                                            borderRadius: 2,
                                            direction: isArabic ? 'rtl' : 'ltr',
                                        },
                                    }}
                                >
                                    {item.children.map((child) => {
                                        const ChildIcon = child.icon;
                                        const childIsActive = location.pathname.startsWith(child.path);
                                        return (
                                            <MenuItem
                                                key={child.key}
                                                selected={childIsActive}
                                                onClick={() => {
                                                    navigate(child.path);
                                                    closeMenu();
                                                }}
                                                sx={{ py: 0.85 }}
                                            >
                                                {ChildIcon && (
                                                    <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                                                        <ChildIcon fontSize="small" />
                                                    </ListItemIcon>
                                                )}
                                                <ListItemText
                                                    primary={child.label?.[lng] || child.label?.en}
                                                    primaryTypographyProps={{ fontSize: 14 }}
                                                />
                                            </MenuItem>
                                        );
                                    })}
                                </Menu>
                            )}
                        </React.Fragment>
                    );
                })}
            </Box>
        </Box>
    );
};

export default HorizontalNav;
